import type {
  Background,
  Connection,
  Container,
  Dimensions,
  Dimensions2D,
  Dimensions3D,
  Landscape,
  PCS,
  Positionable,
  Skybox,
  Specifications,
  StaticData,
  Textures,
  TextureSet,
  Vector3,
} from '@/siteview/entities/static-data';
import type { DeepUnknown } from '@/types/deep-unknown';

export function staticDataFromApi(data: any): DeepUnknown<StaticData> {
  if (!data) return undefined;

  return {
    landscape: landscapeFromApi(data?.landscape),
    containers: data?.containers?.map(containersFromApi),
    background: backgroundFromApi(data?.background),
    connections: data?.connections?.map(connectionFromApi),
    pcs: data?.pcs?.map(pcsFromApi),
    specifications: specificationsFromApi(data?.specifications),
  };
}

export function landscapeFromApi(data: any): DeepUnknown<Landscape> {
  if (!data) {
    return {
      backgroundImageAbstract: undefined,
      backgroundImageSatellite: undefined,
    };
  }

  return {
    backgroundImageAbstract: data?.background_image_abstract,
    backgroundImageSatellite: data?.background_image_satellite,
  };
}

export function containersFromApi(data: any): DeepUnknown<Container> {
  if (!data) return undefined;

  return {
    uid: data?.uid,
    label: data?.label,
    position: vector3FromApi(data?.position),
    rotation: vector3FromApi(data?.rotation),
    dimensionsOverride: dimensions3DFromApi(data?.dimensions_override),
    textureOverride: textureSetFromApi(data?.texture_override),
    rackStrings: data?.rack_strings?.map(positionableFromApi),
    racks: data?.racks?.map(positionableFromApi),
    modules: data?.modules?.map(positionableFromApi),
    doors: data?.doors?.map(positionableFromApi),
    vents: data?.vents?.map(positionableFromApi),
  };
}

export function vector3FromApi(data: any): DeepUnknown<Vector3> {
  if (!data) return undefined;

  return {
    x: data?.x,
    y: data?.y,
    z: data?.z,
  };
}

export function dimensions3DFromApi(data: any): DeepUnknown<Dimensions3D> {
  if (!data) return undefined;

  return {
    depth: data?.depth,
    height: data?.height,
    width: data?.width,
  };
}

export function dimensions2DFromApi(data: any): DeepUnknown<Dimensions2D> {
  if (!data) return undefined;

  return {
    width: data?.width,
    height: data?.height,
  };
}
export function textureSetFromApi(data: any): DeepUnknown<TextureSet> {
  if (!data) return undefined;

  return {
    front: data?.front,
    back: data?.back,
    left: data?.left,
    right: data?.right,
    top: data?.top,
  };
}

export function positionableFromApi(data: any): DeepUnknown<Positionable> {
  if (!data) return undefined;

  return {
    uid: data?.uid,
    position: vector3FromApi(data?.position),
    rotation: vector3FromApi(data?.rotation),
    dimensionsOverride: dimensions3DFromApi(data.dimensions_override),
  };
}

export function backgroundFromApi(data: any): DeepUnknown<Background> {
  if (!data) return undefined;

  return {
    skybox: skyboxFromApi(data?.skybox),
    sphere_skybox: data?.sphere_skybox,
    color: data?.color,
    image: data?.image,
  };
}

export function skyboxFromApi(data: any): DeepUnknown<Skybox> {
  if (!data) return undefined;

  return {
    front: data?.front,
    back: data?.back,
    left: data?.left,
    right: data?.right,
    top: data?.top,
    bottom: data?.bottom,
  };
}

export function connectionFromApi(data: any): DeepUnknown<Connection> {
  if (!data) return undefined;

  return {
    uid: data?.uid,
    containers: data?.containers,
    pcs: data?.pcs,
    diameterOverride: data?.diameter_override,
  };
}

export function pcsFromApi(data: any): DeepUnknown<PCS> {
  if (!data) return undefined;

  return {
    uid: data?.uid,
    label: data?.label,
    position: vector3FromApi(data?.position),
    rotation: vector3FromApi(data?.rotation),
    dimensionsOverride: dimensions3DFromApi(data?.dimensions_override),
    doors: data?.doors?.map(positionableFromApi),
    textureOverride: textureSetFromApi(data?.texture_override),
  };
}

export function specificationsFromApi(data: any): DeepUnknown<Specifications> {
  if (!data) return undefined;

  return {
    dimensions: dimensionsFromApi(data?.dimensions),
    textures: texturesFromApi(data?.textures),
  };
}

export function dimensionsFromApi(data: any): DeepUnknown<Dimensions> {
  if (!data) return undefined;

  return {
    Landscape: dimensions2DFromApi(data?.Landscape),
    BESS: dimensions3DFromApi(data?.BESS),
    String: dimensions3DFromApi(data?.String),
    PCS: dimensions3DFromApi(data?.PCS),
    Rack: dimensions3DFromApi(data?.Rack),
    Vent: dimensions3DFromApi(data?.Vent),
    Module: dimensions3DFromApi(data?.Module),
    PCSDoor: dimensions3DFromApi(data?.PCSDoor),
    BESSDoor: dimensions3DFromApi(data?.BESSDoor),
    ConnectionDiameter: undefined, // INFO: wait for the API to implement that
  };
}

export function texturesFromApi(data: any): DeepUnknown<Textures> {
  if (!data) return undefined;

  return {
    BESS: textureSetFromApi(data?.BESS),
    PCS: textureSetFromApi(data?.PCS),
  };
}
